import React from "react";
import { Pencil } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { ConfigField, ConfigSection } from "config/adminConfig";
import { ConfigItem } from "services/AdminConfig/adminConfigService";

interface ConfigTableProps {
  section: ConfigSection;
  fieldItems: Record<string, ConfigItem[]>;
  loadingFields: Set<string>;
}

const Spinner = () => (
  <svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 100 16v-4l-3 3 3 3v-4a8 8 0 01-8-8z" />
  </svg>
);

const ConfigTable: React.FC<ConfigTableProps> = ({ section, fieldItems, loadingFields }) => {
  const navigate = useNavigate();

  const handleEdit = (field: ConfigField) => {
    // Strip "api/" prefix to form the route segment
    const slug = field.apiEndpoint.replace("api/", "");
    navigate(`/admin/admin-config/${slug}`);
  };

  return (
    <div className="flex flex-col flex-1 min-h-0 bg-white dark:bg-gray-800 rounded-xl shadow-sm">
      <div className="px-6 py-5 border-b border-gray-100 dark:border-gray-700">
        <h1 className="text-lg font-semibold text-gray-900 dark:text-white">Configuration</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
          Manage {section.label.toLowerCase()} configuration values
        </p>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto">
        <table className="w-full text-sm text-left" style={{ tableLayout: "fixed" }}>
          <colgroup>
            <col style={{ width: "34%" }} />
            <col style={{ width: "33%" }} />
            <col style={{ width: "33%" }} />
          </colgroup>
          <thead className="sticky top-0 bg-gray-50 dark:bg-gray-700/50 z-10">
            <tr>
              {["Name", "Status", "Action"].map((col) => (
                <th
                  key={col}
                  className="px-6 py-3.5 font-medium text-gray-500 dark:text-gray-400 text-xs uppercase tracking-wider"
                >
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {section.fields.map((field) => {
              const isLoading = loadingFields.has(field.key);
              const items = fieldItems[field.key];
              const hasValues = items && items.length > 0;

              return (
                <tr
                  key={field.key}
                  className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors"
                >
                  <td className="px-6 py-4 font-medium text-gray-900 dark:text-white">
                    {field.label}
                  </td>

                  <td className="px-6 py-4 overflow-hidden">
                    {isLoading ? (
                      <span className="inline-flex items-center gap-1.5 text-gray-400 text-xs">
                        <Spinner />
                        Loading…
                      </span>
                    ) : (
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          hasValues
                            ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                            : "bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400"
                        }`}
                      >
                        {hasValues ? `${items.length} active` : "Empty"}
                      </span>
                    )}
                  </td>

                  <td className="px-6 py-4">
                    <button
                      onClick={() => handleEdit(field)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                    >
                      <Pencil size={13} />
                      Edit
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ConfigTable;
